import { useAppStore } from './hooks/useAppStore';
import { LoginScreen } from './components/layout/LoginScreen';
import { AppHeader } from './components/layout/AppHeader';
import { TabNav } from './components/layout/TabNav';
import { AppFooter } from './components/layout/AppFooter';
import { Toast } from './components/modals/Toast';
import { TasksTab } from './components/tabs/TasksTab';
import { ReviewTab } from './components/tabs/ReviewTab';
import { PeerEvalTab } from './components/tabs/PeerEvalTab';
import { CalibrationTab } from './components/tabs/CalibrationTab';
import { MonitorTab } from './components/tabs/MonitorTab';
import { AdminTab } from './components/tabs/AdminTab';
import { RegisterTaskModal } from './components/modals/RegisterTaskModal';
import { PasswordChangeModal } from './components/modals/PasswordChangeModal';
import { CalibrationModal } from './components/modals/CalibrationModal';

export default function App() {
    const store = useAppStore();
    const { sessionUser } = store;

    // -------------------------------------------------------------
    // A. 로그인 전: 포털 로그인 화면
    // -------------------------------------------------------------
    if (!sessionUser) {
        return (
            <LoginScreen
                firebaseUser={store.firebaseUser}
                loginName={store.loginName}
                setLoginName={store.setLoginName}
                loginPassword={store.loginPassword}
                setLoginPassword={store.setLoginPassword}
                loginError={store.loginError}
                isAuthBusy={store.isAuthBusy}
                handlePasswordLogin={store.handlePasswordLogin}
                handleRegisterAndLogin={store.handleRegisterAndLogin}
                newMemberName={store.newMemberName}
                setNewMemberName={store.setNewMemberName}
                newMemberOrg={store.newMemberOrg}
                setNewMemberOrg={store.setNewMemberOrg}
                newMemberTeam={store.newMemberTeam}
                setNewMemberTeam={store.setNewMemberTeam}
                newMemberDept={store.newMemberDept}
                setNewMemberDept={store.setNewMemberDept}
                orgTeamList={store.orgTeamList}
                newMemberPassword={store.newMemberPassword}
                setNewMemberPassword={store.setNewMemberPassword}
                newMemberPasswordConfirm={store.newMemberPasswordConfirm}
                setNewMemberPasswordConfirm={store.setNewMemberPasswordConfirm}
                toastMessage={store.toastMessage}
                publicRegistrationOpen={store.publicRegistrationOpen}
            />
        );
    }

    // -------------------------------------------------------------
    // B. 로그인 후: 성과 및 평가 플랫폼 본문
    // -------------------------------------------------------------
    return (
        <div className="min-h-screen flex flex-col">
            <AppHeader
                sessionUser={sessionUser}
                evalYear={store.evalYear}
                evalYearOptions={store.evalYearOptions}
                changeEvalYear={store.changeEvalYear}
                openPwModal={store.openPwModal}
                handleLogout={store.handleLogout}
            />

            <main className="flex-grow max-w-7xl w-full mx-auto px-4 py-8">
                <div className="mb-6 p-4 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-3 w-3 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-600"></span>
                        </span>
                        <span className="text-sm font-medium text-indigo-900">
                            반려/조정, 성과 갱신, 다면평가 등 모든 정보는 클라우드 데이터베이스(Cloud Firestore)에 즉시 기록되어 안전하게 보존됩니다.
                        </span>
                    </div>
                    <div className="text-xs text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full font-bold hidden sm:block">
                        Cloud Realtime Sync Active
                    </div>
                </div>

                <TabNav activeTab={store.activeTab} setActiveTab={store.setActiveTab} role={sessionUser.role} />

                {store.activeTab === 'tasks' && (
                    <TasksTab
                        sessionUser={sessionUser}
                        visibleTasks={store.visibleTasks}
                        membersById={store.membersById}
                        onOpenRegister={() => store.setIsRegisterOpen(true)}
                        handleUpdateStatus={store.handleUpdateStatus}
                    />
                )}

                {store.activeTab === 'review' && (
                    <ReviewTab
                        sessionUser={sessionUser}
                        visibleTasks={store.visibleTasks}
                        handleQuantitativeChange={store.handleQuantitativeChange}
                        handleQualitativeCheckToggle={store.handleQualitativeCheckToggle}
                        handleConfirmQuarter={store.handleConfirmQuarter}
                        handleUnlockQuarter={store.handleUnlockQuarter}
                    />
                )}

                {store.activeTab === 'peer-eval' && (
                    <PeerEvalTab
                        sessionUser={sessionUser}
                        myEvaluationTargets={store.myEvaluationTargets}
                        myGivenAnswers={store.myGivenAnswers}
                        mySubmittedColleagueIds={store.mySubmittedColleagueIds}
                        activeColleagueToEvaluate={store.activeColleagueToEvaluate}
                        setActiveColleagueToEvaluate={store.setActiveColleagueToEvaluate}
                        handleAnswerSelect={store.handleAnswerSelect}
                        commentStrength={store.commentStrength}
                        setCommentStrength={store.setCommentStrength}
                        commentImprovement={store.commentImprovement}
                        setCommentImprovement={store.setCommentImprovement}
                        handleSaveDraft={store.handleSaveDraft}
                        handleCompleteEvaluation={store.handleCompleteEvaluation}
                        receivedParticipants={store.receivedParticipants}
                        receivedByCategory={store.receivedByCategory}
                        receivedStrengths={store.receivedStrengths}
                        receivedImprovements={store.receivedImprovements}
                        selectedPoolSubject={store.selectedPoolSubject}
                        setSelectedPoolSubject={store.setSelectedPoolSubject}
                        departmentMembers={store.departmentMembers}
                        evaluatorPools={store.evaluatorPools}
                        currentPool={store.currentPool}
                        currentPoolCount={store.currentPoolCount}
                        poolRecommendedIds={store.poolRecommendedIds}
                        poolRecommendedSet={store.poolRecommendedSet}
                        handleApplyRecommendedPool={store.handleApplyRecommendedPool}
                        handleTogglePoolMember={store.handleTogglePoolMember}
                        handleConfirmEvaluatorPool={store.handleConfirmEvaluatorPool}
                        handleUnlockEvaluatorPool={store.handleUnlockEvaluatorPool}
                    />
                )}

                {store.activeTab === 'calibration' && (
                    <CalibrationTab
                        departmentMembers={store.departmentMembers}
                        departments={store.departments}
                        selectedDeptFilter={store.selectedDeptFilter}
                        setSelectedDeptFilter={store.setSelectedDeptFilter}
                        isLenientWarning={store.isLenientWarning}
                        exPercentage={store.exPercentage}
                        membersForCalibration={store.membersForCalibration}
                        exCount={store.exCount}
                        totalMembers={store.totalMembers}
                        openCalibrationModal={store.openCalibrationModal}
                        peerReportByMember={store.peerReportByMember}
                    />
                )}

                {store.activeTab === 'monitor' && sessionUser.role === 'HR' && store.hrMonitor && (
                    <MonitorTab hrMonitor={store.hrMonitor} />
                )}

                {store.activeTab === 'admin' && sessionUser.role === 'HR' && (
                    <AdminTab
                        evalYear={store.evalYear}
                        seasonLock={store.seasonLock}
                        handleSaveSeasonLock={store.handleSaveSeasonLock}
                        publicRegistrationOpen={store.publicRegistrationOpen}
                        handleSetPublicRegistration={store.handleSetPublicRegistration}
                        departmentMembers={store.departmentMembers}
                        orgTeamList={store.orgTeamList}
                        handleRunDbAudit={store.handleRunDbAudit}
                        handleExportToExcel={store.handleExportToExcel}
                        handleAddDepartment={store.handleAddDepartment}
                        newDeptOrg={store.newDeptOrg}
                        setNewDeptOrg={store.setNewDeptOrg}
                        newDeptTeam={store.newDeptTeam}
                        setNewDeptTeam={store.setNewDeptTeam}
                        handleAddMember={store.handleAddMember}
                        newMemberName={store.newMemberName}
                        setNewMemberName={store.setNewMemberName}
                        adminMemberOrg={store.adminMemberOrg}
                        setAdminMemberOrg={store.setAdminMemberOrg}
                        adminMemberTeam={store.adminMemberTeam}
                        setAdminMemberTeam={store.setAdminMemberTeam}
                        adminMemberDept={store.adminMemberDept}
                        setAdminMemberDept={store.setAdminMemberDept}
                        newMemberDuty={store.newMemberDuty}
                        setNewMemberDuty={store.setNewMemberDuty}
                        newMemberEvalGroup={store.newMemberEvalGroup}
                        setNewMemberEvalGroup={store.setNewMemberEvalGroup}
                        adminInitPassword={store.adminInitPassword}
                        setAdminInitPassword={store.setAdminInitPassword}
                        newMemberPerf={store.newMemberPerf}
                        setNewMemberPerf={store.setNewMemberPerf}
                        newMemberBehav={store.newMemberBehav}
                        setNewMemberBehav={store.setNewMemberBehav}
                        newMemberNote={store.newMemberNote}
                        setNewMemberNote={store.setNewMemberNote}
                        handleDeleteMember={store.handleDeleteMember}
                        handleSetMemberDisabled={store.handleSetMemberDisabled}
                    />
                )}
            </main>

            <AppFooter />

            {store.selectedMemberForCalibration && (
                <CalibrationModal
                    selectedMemberForCalibration={store.selectedMemberForCalibration}
                    onClose={() => store.setSelectedMemberForCalibration(null)}
                    peerReportByMember={store.peerReportByMember}
                    editPerfGrade={store.editPerfGrade}
                    setEditPerfGrade={store.setEditPerfGrade}
                    editBehaviorGrade={store.editBehaviorGrade}
                    setEditBehaviorGrade={store.setEditBehaviorGrade}
                    editNote={store.editNote}
                    setEditNote={store.setEditNote}
                    applyPeerBehaviorSuggest={store.applyPeerBehaviorSuggest}
                    handleSaveCalibration={store.handleSaveCalibration}
                    evalYear={store.evalYear}
                />
            )}

            {store.isRegisterOpen && (
                <RegisterTaskModal
                    departmentMembers={store.departmentMembers}
                    sessionUser={sessionUser}
                    onClose={() => store.setIsRegisterOpen(false)}
                    onSubmit={store.handleTaskSubmit}
                />
            )}

            {store.isPwModalOpen && (
                <PasswordChangeModal
                    onClose={() => store.setIsPwModalOpen(false)}
                    handleChangePassword={store.handleChangePassword}
                    pwCurrent={store.pwCurrent}
                    setPwCurrent={store.setPwCurrent}
                    pwNew={store.pwNew}
                    setPwNew={store.setPwNew}
                    pwNewConfirm={store.pwNewConfirm}
                    setPwNewConfirm={store.setPwNewConfirm}
                    pwError={store.pwError}
                />
            )}

            <Toast message={store.toastMessage} />
        </div>
    );
}
